import { jwtDecode } from 'jwt-decode';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import type { AuthClient, TokenRefreshedDetail } from './auth-client.js';

/**
 * Auth-State für beide React-Apps. Übernommen aus Atleos `AuthContext`, mit zwei Änderungen:
 *
 * - Der `AuthClient` kommt als Prop herein, statt als Modul-Singleton importiert zu werden —
 *   sonst könnten `apps/web` und `apps/admin` nicht unterschiedliche Login-Ziele haben.
 * - Die Sentry-Kopplung ist entfallen (D22: kein Sentry im MVP).
 *
 * Beibehalten ist der proaktive Refresh fünf Minuten vor Ablauf. Ohne ihn läuft der erste
 * Request nach einer Pause zuverlässig in einen 401 und der Nutzer sieht einen Spinner, der
 * sich nicht erklären lässt.
 */

/** Claims, die caterinx im ID-Token führt. */
export interface AuthUser {
  sub: string;
  email: string;
  /** Bei Lieferanten-Nutzern gesetzt; bei Plattform-Admins nicht. */
  'custom:supplierId'?: string;
  /** Team-Rolle innerhalb des Lieferanten (`owner` | `admin` | `member`). */
  'custom:role'?: string;
  /** Cognito-Gruppen. `SystemAdmin` steuert den Admin-Bereich — serverseitig geprüft. */
  'cognito:groups'?: string[];
  exp?: number;
  [key: string]: unknown;
}

interface AuthContextValue {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  setToken: (token: string | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  /** `true`, während der erste Token aus dem localStorage geladen wird — verhindert Flackern. */
  isAuthLoading: boolean;
  logout: () => void;
  client: AuthClient;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function decodeToken(jwt: string | null): AuthUser | null {
  if (!jwt) return null;
  try {
    return jwtDecode<AuthUser>(jwt);
  } catch {
    return null;
  }
}

export function AuthProvider({ client, children }: { client: AuthClient; children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null);
  const [refreshToken, setRefreshTokenState] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  // Einmal beim Start: vorhandene Token aus dem Speicher übernehmen.
  useEffect(() => {
    const storedToken = client.getToken();
    setTokenState(storedToken);
    setRefreshTokenState(client.getRefreshToken());
    setUser(decodeToken(storedToken));
    setIsAuthLoading(false);
  }, [client]);

  // Refresh durch den Axios-Interceptor: React-State nachziehen.
  useEffect(() => {
    const handleTokenRefreshed = (event: Event): void => {
      const detail = (event as CustomEvent<TokenRefreshedDetail>).detail;
      setTokenState(detail.token);
      setUser(decodeToken(detail.token));
    };
    window.addEventListener(client.tokenRefreshedEvent, handleTokenRefreshed);
    return () => window.removeEventListener(client.tokenRefreshedEvent, handleTokenRefreshed);
  }, [client]);

  // Proaktiver Refresh fünf Minuten vor Ablauf.
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleProactiveRefresh = useCallback(
    (jwt: string | null): void => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (!jwt) return;

      const decoded = decodeToken(jwt);
      if (!decoded?.exp) return;

      const secondsUntilExpiry = decoded.exp - Date.now() / 1000;
      const refreshInMs = Math.max((secondsUntilExpiry - 5 * 60) * 1000, 0);

      refreshTimerRef.current = setTimeout(() => {
        void client.refreshAuthToken().then((newToken) => {
          if (!newToken) return;
          setTokenState(newToken);
          setUser(decodeToken(newToken));
          scheduleProactiveRefresh(newToken);
        });
      }, refreshInMs);
    },
    [client]
  );

  useEffect(() => {
    scheduleProactiveRefresh(token);
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [token, scheduleProactiveRefresh]);

  const setToken = useCallback(
    (newToken: string | null): void => {
      setTokenState(newToken);
      setUser(decodeToken(newToken));
      client.setToken(newToken);
    },
    [client]
  );

  const setRefreshToken = useCallback(
    (newRefreshToken: string | null): void => {
      setRefreshTokenState(newRefreshToken);
      client.setRefreshToken(newRefreshToken);
    },
    [client]
  );

  const logout = useCallback((): void => {
    setToken(null);
    setRefreshToken(null);
  }, [setToken, setRefreshToken]);

  return (
    <AuthContext.Provider
      value={{ token, refreshToken, user, setToken, setRefreshToken, isAuthLoading, logout, client }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext muss innerhalb eines AuthProvider verwendet werden');
  }
  return context;
}

/** Wahr, wenn der angemeldete Nutzer in der Cognito-Gruppe `SystemAdmin` ist. */
export function useIsSystemAdmin(): boolean {
  const { user } = useAuthContext();
  return user?.['cognito:groups']?.includes('SystemAdmin') ?? false;
}
